/**
 * F2.2a — Présentation catalogue Récompenses (lecture seule).
 * Aucun champ technique exposé (id, JSON, sourceLegacy, jargon).
 */

export const FIDELITE_SECTION_PROGRAMME = 'programme';
export const FIDELITE_SECTION_RECOMPENSES = 'recompenses';

export const EMPTY_STATE_TITLE = 'Aucune récompense pour l’instant.';
export const EMPTY_STATE_BODY =
  'Les clients peuvent cumuler des points, mais ils ne peuvent pas encore les échanger.';

export const PAGE_TITLE = 'Récompenses';
export const PAGE_SUBTITLE =
  'Définissez ce que vos clients peuvent obtenir en échange de leurs points.';

const STATUT_LABELS = {
  BROUILLON: 'Brouillon',
  ACTIVE: 'Active',
  SUSPENDUE: 'Suspendue',
  ARCHIVEE: 'Archivée',
  PLANIFIEE: 'Planifiée',
  EXPIREE: 'Expirée',
};

/**
 * @param {string|null|undefined} sectionFromUrl
 * @returns {'programme'|'recompenses'}
 */
export function resolveFideliteSection(sectionFromUrl) {
  return sectionFromUrl === FIDELITE_SECTION_RECOMPENSES
    ? FIDELITE_SECTION_RECOMPENSES
    : FIDELITE_SECTION_PROGRAMME;
}

/**
 * Libellé d’affichage pour statut persisté ou statutAffiche dérivé.
 */
export function formatStatutLabel(statutOrAffiche) {
  if (!statutOrAffiche || typeof statutOrAffiche !== 'string') return '—';
  return STATUT_LABELS[statutOrAffiche] || statutOrAffiche;
}

/**
 * Badge principal : préfère statutAffiche (PLANIFIEE/EXPIREE) s’il diffère.
 */
export function resolveDisplayStatut(recompense) {
  if (!recompense || typeof recompense !== 'object') {
    return { code: null, label: '—' };
  }
  const affiche = recompense.statutAffiche || recompense.statut;
  return {
    code: affiche || null,
    label: formatStatutLabel(affiche),
    persisted: recompense.statut || null,
  };
}

function effectsOf(recompense) {
  if (Array.isArray(recompense?.effets)) return recompense.effets;
  if (Array.isArray(recompense?.effects)) return recompense.effects;
  return [];
}

function formatNumericDisplay(v) {
  if (!Number.isFinite(v)) return '';
  if (Number.isInteger(v)) return String(v);
  const rounded = Math.round(v * 100) / 100;
  let s = String(rounded);
  if (s.includes('.')) {
    s = s.replace(/(\.\d*?[1-9])0+$/, '$1').replace(/\.0+$/, '');
  }
  return s.replace('.', ',');
}

/**
 * Famille d’effet pour cartes UX (extensible % / cadeau — sans inventer de données).
 * @returns {{ family: string, typeLabel: string, hero: string, effectLabel: string, effectTitle: string }}
 */
export function resolveEffectFamily(recompense) {
  const effects = effectsOf(recompense);
  const libelle =
    typeof recompense?.libelle === 'string' && recompense.libelle.trim()
      ? recompense.libelle.trim()
      : null;
  if (effects.length === 0) {
    return {
      family: 'unknown',
      typeLabel: 'Récompense',
      hero: '•',
      effectLabel: 'Effet non précisé',
      effectTitle: libelle || 'RÉCOMPENSE',
    };
  }
  const first = effects[0];
  if (!first || typeof first !== 'object') {
    return {
      family: 'unknown',
      typeLabel: 'Récompense',
      hero: '•',
      effectLabel: 'Effet non précisé',
      effectTitle: libelle || 'RÉCOMPENSE',
    };
  }
  if (first.type === 'REDUCTION_EUR') {
    const v = Number(first.valeur);
    if (Number.isFinite(v) && v > 0) {
      const formatted = formatNumericDisplay(v);
      return {
        family: 'montant',
        typeLabel: 'Montant offert',
        hero: `${formatted} €`,
        effectLabel: `${formatted} € offerts`,
        effectTitle: `${formatted} € OFFERTS`,
      };
    }
    return {
      family: 'montant',
      typeLabel: 'Montant offert',
      hero: '€',
      effectLabel: 'Réduction en euros',
      effectTitle: 'MONTANT OFFERT',
    };
  }
  // F2.5a2 — canon REDUCTION_POURCENT (+ alias lecture REDUCTION_PCT)
  if (first.type === 'REDUCTION_POURCENT' || first.type === 'REDUCTION_PCT') {
    const v = Number(first.valeur);
    if (Number.isFinite(v) && v > 0) {
      const formatted = formatNumericDisplay(v);
      return {
        family: 'pourcent',
        typeLabel: 'Réduction en %',
        hero: `${formatted} %`,
        effectLabel: `${formatted} % de réduction`,
        effectTitle: `${formatted} % DE RÉDUCTION`,
      };
    }
    return {
      family: 'pourcent',
      typeLabel: 'Réduction en %',
      hero: '%',
      effectLabel: 'Réduction en pourcentage',
      effectTitle: 'RÉDUCTION',
    };
  }
  return {
    family: 'cadeau',
    typeLabel: 'Cadeau',
    hero: '🎁',
    effectLabel: libelle || 'Récompense',
    effectTitle: (libelle || 'CADEAU').toUpperCase(),
  };
}

/**
 * Effet principal lisible — jamais le JSON brut.
 */
export function formatPrimaryEffect(recompense) {
  return resolveEffectFamily(recompense).effectLabel;
}

export function formatCoutPoints(coutPoints) {
  const n = Number(coutPoints);
  if (!Number.isFinite(n) || n < 0) return '—';
  const rounded = Math.round(n);
  return rounded === 1 ? '1 point' : `${rounded} points`;
}

function formatDateFr(value) {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  try {
    return d.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return null;
  }
}

/**
 * Période lisible si dates configurées.
 */
export function formatPeriode(recompense) {
  const from = formatDateFr(recompense?.validFrom);
  const to = formatDateFr(recompense?.validTo);
  if (!from && !to) return null;
  if (from && to) return `Du ${from} au ${to}`;
  if (from) return `À partir du ${from}`;
  return `Jusqu’au ${to}`;
}

/**
 * Carte de présentation sûre pour l’UI (sans champs techniques).
 */
export function toRecompenseCardView(recompense, index = 0) {
  if (!recompense || typeof recompense !== 'object') {
    return {
      key: `invalid-${index}`,
      libelle: 'Récompense',
      coutLabel: '—',
      effectLabel: 'Effet non précisé',
      statut: resolveDisplayStatut(null),
      periode: null,
      position: index + 1,
    };
  }
  const statut = resolveDisplayStatut(recompense);
  const family = resolveEffectFamily(recompense);
  return {
    key: recompense.id ? String(recompense.id) : `row-${index}`,
    libelle:
      typeof recompense.libelle === 'string' && recompense.libelle.trim()
        ? recompense.libelle.trim()
        : 'Récompense',
    coutLabel: formatCoutPoints(recompense.coutPoints),
    effectLabel: family.effectLabel,
    effectTitle: family.effectTitle,
    typeLabel: family.typeLabel,
    hero: family.hero,
    family: family.family,
    statut,
    periode: formatPeriode(recompense),
    position:
      recompense.ordre != null && Number.isFinite(Number(recompense.ordre))
        ? Number(recompense.ordre) + 1
        : index + 1,
  };
}

/**
 * Normalise la réponse GET liste (tolérant aux formes partielles).
 */
export function normalizeRecompensesListResponse(payload) {
  if (!payload) return [];
  const raw = Array.isArray(payload)
    ? payload
    : Array.isArray(payload.recompenses)
      ? payload.recompenses
      : Array.isArray(payload.data)
        ? payload.data
        : [];
  return raw.filter((r) => r && typeof r === 'object');
}

/**
 * Clés techniques interdites dans le rendu texte d’une carte (garde-fou tests).
 */
export const FORBIDDEN_TECH_TOKENS = [
  'sourceLegacy',
  'eligibility',
  'effects',
  'effets',
  'commercantId',
  'cuid',
  'Prisma',
  'REDUCTION_EUR',
  'rewardId',
  '"type"',
];

export function cardViewContainsForbiddenTech(card) {
  const blob = JSON.stringify(card);
  return FORBIDDEN_TECH_TOKENS.some((t) => {
    if (t === 'effets' || t === 'effects') {
      // Les clés internes de toRecompenseCardView n'incluent pas effects/effets
      return false;
    }
    return blob.includes(t);
  });
}
